import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { MEDIA_BATCH_SIZE, fetchPresentationMedia } from '../api/presentation-media';
import type { PresentationMedia } from '../api/presentation-media';
import { extractLinks } from '../lib/extract-links';
import { fetchImageBlob } from '../lib/render/fetch-image';
import { renderInsightsScreen } from '../lib/render/insights-canvas';
import type { InsightsData, ScreenKind } from '../lib/render/insights-values';
import {
  buildRows,
  emptyRow,
  periodOf,
  totalsOf,
  type PostRow,
} from '../lib/post-rows';

/**
 * Состояние табличного отчёта по публикациям: акта об услугах и «посева».
 *
 * Строки живут здесь, а не в форме: их собирают по ссылкам, потом правят
 * руками, добавляют свои и подкладывают скриншоты — и всё это одна и та же
 * таблица, у которой должен быть один хозяин.
 */

function insightsOf(media: PresentationMedia): InsightsData {
  return {
    views: media.views ?? 0,
    reach: media.reach ?? 0,
    likes: media.likes ?? 0,
    comments: media.comments ?? 0,
    saved: media.saved ?? 0,
    shares: media.shares,
    reposts: media.reposts,
    totalInteractions: media.totalInteractions,
    profileVisits: media.profileVisits,
    follows: media.follows,
    avgWatchMs: media.avgWatchMs,
    totalWatchMs: media.totalWatchMs,
    skipRate: media.skipRate,
  };
}

function kindOf(media: PresentationMedia): ScreenKind {
  return media.productType === 'REELS' ? 'reel' : 'post';
}

/** У видео и рилса media_url — сам ролик; картинка лежит в thumbnail_url. */
function previewUrl(media: PresentationMedia): string {
  for (const url of [media.thumbnailUrl, media.mediaUrl]) {
    if (url && !/\.mp4(\?|$)/i.test(url)) return url;
  }
  return '';
}

export function usePostRows() {
  const [rows, setRows] = useState<PostRow[]>([]);
  const [progress, setProgress] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const totals = useMemo(() => totalsOf(rows), [rows]);

  const patchRow = useCallback((key: string, patch: Partial<PostRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }, []);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, emptyRow()]);
  }, []);

  /**
   * Ссылки → строки таблицы и экраны статистики.
   *
   * Экраны рисуются сразу, а не при скачивании: пока они рисуются, человек
   * уже видит таблицу и правит цены. Если экран не вышел (CDN не отдал
   * превью), строка остаётся без картинки — в Word на её месте будет
   * пустая рамка, и скриншот вставят руками.
   */
  const collect = useCallback(async (text: string) => {
    const links = extractLinks(text).map((url) => ({ url, shortcode: extractShortcode(url) }));
    if (links.length === 0) {
      toast.error('В списке нет ссылок на публикации');
      return;
    }

    setIsBusy(true);
    try {
      const withCode = links.filter((l) => l.shortcode);
      const media: PresentationMedia[] = [];
      for (let i = 0; i < withCode.length; i += MEDIA_BATCH_SIZE) {
        const part = withCode.slice(i, i + MEDIA_BATCH_SIZE);
        setProgress(
          withCode.length > MEDIA_BATCH_SIZE
            ? `Считаем цифры: ${i + 1}–${Math.min(i + part.length, withCode.length)} из ${withCode.length}`
            : `Считаем цифры по ${withCode.length} ссылкам`,
        );
        const answer = await fetchPresentationMedia(part.map((l) => l.shortcode as string));
        media.push(...answer.media);
      }

      const next = buildRows(links, media);
      setRows(next);
      const period = periodOf(next);
      setFrom(period.from);
      setTo(period.to);

      const missing = next.filter((row) => row.manual).length;
      if (missing > 0) {
        toast.warning(`Без цифр осталось строк: ${missing}. Их заполняют руками.`);
      }

      const byShortcode = new Map(media.map((m) => [m.shortcode, m]));
      let done = 0;
      for (const row of next) {
        const found = row.shortcode ? byShortcode.get(row.shortcode) : undefined;
        if (!found) continue;
        done += 1;
        setProgress(`Рисуем экраны статистики: ${done} из ${media.length}`);
        try {
          const preview = previewUrl(found);
          const screen = await renderInsightsScreen({
            data: insightsOf(found),
            kind: kindOf(found),
            thumb: preview ? await fetchImageBlob(preview) : null,
          });
          setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, screen } : r)));
        } catch (err) {
          console.error('[Отчёт] экран не нарисовался:', err);
        }
      }
    } catch (err) {
      console.error('[Отчёт] сбой сборки:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать таблицу');
    } finally {
      setIsBusy(false);
      setProgress('');
    }
  }, []);

  return {
    rows,
    totals,
    progress,
    isBusy,
    from,
    to,
    setFrom,
    setTo,
    addRow,
    patchRow,
    removeRow,
    collect,
  };
}
